import React, { useState } from "react";

import Input from "../../controls/Input";
import CreatableSelectMenu from "../../controls/CreatableSelectMenu";
import SelectMenu from "../../controls/SelectMenu";

import {
  classNames,
  convertToDefEventPara,
  wipStatusOptions,
  formatDateWithTime,
  generateUniqueNumber,
} from "../../../shared/utility";
import AddItemToReceivingForm from "./AddItemToReceivingForm";
import MediaUi from "../../importMedia/MediaUi";
import TruckInspectionChecklist from "../shared/TruckInspectionChecklist";
import Button from "../../controls/Button";
import DeleteConfirmationForm from "../DeleteConfirmationForm";

export default function ReceivingInvoiceFields(props) {
  const {
    handleInputChange,
    values,
    errors,
    carrierOptions,
    truckOptions,
    fetchPosForTruck,
    supplierOptions,
    productOptions,
    onFetchProductDetails,
    setEmptyModelOpen,
    handleSubmit,
    submitInProgress,
    addItemToReceiving,
    employeeOptions,
    getWarehouses,
    getWarehouseLocations,
    closed,
    setValues,
    isEdit,
    deleteReceivingInvoiceDocument,
    uploadedDocumentResetSignal,
  } = props;

  const [document1Preview, setDocument1Preview] = useState(null);
  const [document2Preview, setDocument2Preview] = useState(null);
  const isExistingInvoice = Boolean(isEdit || Number(values?.id || 0) > 0);

  const clearDocumentLocally = (field) => {
    setValues({
      ...values,
      [field]: null,
      [`${field}_url`]: null,
    });
    if (field === "document_1") {
      setDocument1Preview(null);
    } else {
      setDocument2Preview(null);
    }
  };

  const confirmDeleteDocument = async (field) => {
    const urlKey = `${field}_url`;
    if (values.id && values[urlKey]) {
      const result = await deleteReceivingInvoiceDocument(field);
      if (!result?.success) {
        return;
      }
    }

    clearDocumentLocally(field);
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const getDocumentFileName = (field) => {
    const path = values[field] || values[`${field}_url`];
    if (!path || typeof path !== "string") {
      return field === "document_1" ? "Document 1" : "Document 2";
    }
    const name = path.split("/").pop()?.split("?")[0];
    return name || (field === "document_1" ? "Document 1" : "Document 2");
  };

  const handleDeleteDocument = (field) => {
    if (closed) {
      return;
    }

    const fileName = getDocumentFileName(field);
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => confirmDeleteDocument(field)}
          message={`Are you sure you want to delete ${fileName}?`}
          setOpen={setEmptyModelOpen}
          hideReason={true}
        />
      ),
      open: true,
    });
  };

  const fetchProductDetailsAndOpenPopup = async function (productId) {
    const result = await onFetchProductDetails(productId);
    if (result.success) {
      const productDetails = result.data;

      setEmptyModelOpen({
        open: true,
        childComponent: (
          <AddItemToReceivingForm
            product={productDetails}
            addItemToReceiving={addItemToReceiving}
            supplierNumber={values.supplier_selected.supplier_number}
            getWarehouses={getWarehouses}
            getWarehouseLocations={getWarehouseLocations}
          />
        ),
        size: "sm:max-w-3xl",
      });
    }
  };

  return (
    <div className="pt-6 pb-5 grid grid-cols-1 gap-2">
      <div className="p-4 card-custom space-y-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-7">
          <div>
            <CreatableSelectMenu
              handleInputChange={(e) => {
                if (!e.target.value.__isNew__) {
                  fetchPosForTruck(e.target.value.value);
                }

                handleInputChange(e);
              }}
              name="truck_selected"
              options={truckOptions}
              value={values.truck_selected}
              title={"Truck #"}
              error={errors.truck_selected}
              disabled={closed}
            />
            <button
              type="button"
              className="mt-1 text-sm text-purple-600 font-bold"
              onClick={() => {
                const generatedTruckNumber = generateUniqueNumber();
                handleInputChange(
                  convertToDefEventPara("truck_selected", {
                    value: 0,
                    label: generatedTruckNumber,
                    __isNew__: true,
                  })
                );
              }}
            >
              Generate
            </button>
          </div>
          <Input
            label="Invoice #"
            name="invoice_number"
            onChange={handleInputChange}
            value={values.invoice_number}
            labelOn={true}
            type="text"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            error={errors.invoice_number}
            disabled={closed}
          />
          <SelectMenu
            handleInputChange={handleInputChange}
            name="supplier_selected"
            options={supplierOptions}
            value={values.supplier_selected}
            title={"Supplier"}
            error={errors.supplier_selected}
            parentClass="sm:col-span-2 xl:col-span-2"
            disabled={closed}
          />
          <SelectMenu
            handleInputChange={handleInputChange}
            name="carrier_selected"
            options={carrierOptions}
            value={values.carrier_selected}
            title={"Carrier"}
            error={errors.carrier_selected}
            disabled={closed}
          />
          <Input
            label="Created"
            name="created_date"
            value={formatDateWithTime(values.created_date)}
            labelOn={true}
            type="text"
            disabled={true}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
            error={errors.created_date}
          />
          <SelectMenu
            name="statusSelected"
            options={wipStatusOptions}
            value={values.statusSelected}
            handleInputChange={handleInputChange}
            title={"Status"}
            error={errors.statusSelected}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <SelectMenu
            handleInputChange={handleInputChange}
            name="created_by"
            options={employeeOptions}
            value={values.created_by}
            title={"Created by"}
            error={errors.created_by}
            disabled={closed}
          />
          <SelectMenu
            handleInputChange={(e) => {
              fetchProductDetailsAndOpenPopup(e.target.value.value);
              handleInputChange(e);
            }}
            name="product_selected"
            options={productOptions}
            value={values.product_selected}
            title={"Products"}
            parentClass={classNames(
              values.id > 0 ? "sm:col-span-2 xl:col-span-2" : "hidden"
            )}
            disabled={closed}
          />
          <div className="sm:col-span-2 xl:col-span-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <MediaUi
                title="Document 1"
                uploadedImage={document1Preview}
                setUploadedImage={setDocument1Preview}
                isEdit={true}
                imageUrl={values.document_1_url}
                imageName="document_1"
                handleInputChange={handleInputChange}
                resetUploadedImageSignal={uploadedDocumentResetSignal}
                onDelete={() => handleDeleteDocument("document_1")}
              />
              <MediaUi
                title="Document 2"
                uploadedImage={document2Preview}
                setUploadedImage={setDocument2Preview}
                isEdit={true}
                imageUrl={values.document_2_url}
                imageName="document_2"
                handleInputChange={handleInputChange}
                resetUploadedImageSignal={uploadedDocumentResetSignal}
                onDelete={() => handleDeleteDocument("document_2")}
              />
            </div>
          </div>
        </div>
        <TruckInspectionChecklist
          values={values}
          errors={errors}
          handleInputChange={handleInputChange}
          disabled={closed}
        />
        <Button
          type="submit"
          className={classNames(
            !isEdit && Number(values?.id || 0) > 0 ? "hidden" : "w-full sm:w-auto",
          )}
          onClick={handleSubmit}
          loading={submitInProgress}
        >
          {isExistingInvoice ? "Save" : "Create"}
        </Button>
      </div>
    </div>
  );
}
